import { DeleteOutlined } from '@ant-design/icons';
import { Space, Switch, Table, TableColumnsType, Typography } from 'antd';
import { AppliedAdditionalCharge } from 'store/invoices/types';
import { StyledInvoicesTableWrapper } from 'views/invoices/settings/styles';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { getIrelandTaxCodePercents } from 'store/invoice-config/types';
import { getEffectiveUnitPrice } from 'utils/InvoiceUtils';
import QuantityCell from './QuantityCell';
import NetAmountCell from './NetAmountCell';
import TaxAmountCell from './TaxAmountCell';

type Props = {
    additionalCharges: AppliedAdditionalCharge[];
    isReadOnly: boolean;
    liabilityGross: number;
    onToggle?: (index: number) => void;
    onDelete?: (record: AppliedAdditionalCharge) => void;
};

const buildColumns = (
    isReadOnly: boolean,
    liabilityGross: number,
    onToggle: (index: number) => void,
    onDelete?: (record: AppliedAdditionalCharge) => void
): TableColumnsType<AppliedAdditionalCharge> => {
    const columns: TableColumnsType<AppliedAdditionalCharge> = [
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 60,
            render: (_quantity: number, _record, index) => <QuantityCell index={index} isReadOnly={isReadOnly} />,
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 1,
            render: (_, record) => {
                const dataTestId = 'code-row';
                return (
                    <Typography.Text data-testid={dataTestId} style={{ whiteSpace: 'nowrap' }}>
                        {record.productCode}
                    </Typography.Text>
                );
            },
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            render: (text: string) => {
                const dataTestId = 'description-row';
                return <Typography.Text data-testid={dataTestId}>{text}</Typography.Text>;
            },
        },
        {
            title: 'Price',
            key: 'price',
            dataIndex: 'unitPrice',
            align: 'right',
            width: 120,
            render: (_unitPrice: number, record) => {
                const dataTestId = 'price-row';
                const price = getEffectiveUnitPrice(record, liabilityGross);
                return <Typography.Text data-testid={dataTestId}>{price.toFixed(2)}</Typography.Text>;
            },
        },
        {
            title: 'Net Amount',
            dataIndex: 'netAmount',
            key: 'netAmount',
            align: 'right',
            width: 120,
            render: (_, _record, index) => {
                const dataTestId = 'netAmount-row';
                return <NetAmountCell dataTestId={dataTestId} index={index} liabilityGross={liabilityGross} />;
            },
        },
        {
            title: 'Tax Rate %',
            dataIndex: 'taxPercent',
            key: 'taxPercent',
            align: 'right',
            width: 120,
            render: (_, record) => {
                const dataTestId = 'taxRate-row';
                const percents = getIrelandTaxCodePercents(record.taxCode);
                const formatted = `${record.taxCode} - ${percents}%`;
                return <Typography.Text data-testid={dataTestId}>{formatted}</Typography.Text>;
            },
        },
        {
            title: 'Tax Amount',
            dataIndex: 'taxAmount',
            key: 'taxAmount',
            align: 'right',
            width: 120,
            render: (_, _record, index) => {
                const dataTestId = 'taxAmount-row';
                return <TaxAmountCell dataTestId={dataTestId} index={index} liabilityGross={liabilityGross} />;
            },
        },
    ];

    if (onDelete && !isReadOnly) {
        columns.push({
            key: 'actions',
            align: 'right',
            width: 60,
            render: (_, _record, index) => {
                const dataTestId = 'delete-charge-row';

                if (_record.customerId) {
                    return (
                        <Space size={8} data-testid={dataTestId}>
                            <Switch size="small" checked={_record.active} onChange={() => onToggle(index)} />
                        </Space>
                    );
                } else {
                    return (
                        <Space size={8} data-testid={dataTestId}>
                            <IconButton
                                onClick={() => onDelete(_record)}
                                data-testid={dataTestId}
                                tooltipText="Remove charge"
                                icon={<DeleteOutlined />}
                            />
                        </Space>
                    );
                }
            },
        });
    }

    return columns;
};

const AppliedAdditionalChargesTable = ({
    isReadOnly,
    additionalCharges,
    liabilityGross,
    onToggle,
    onDelete,
}: Props) => {
    const columns = buildColumns(isReadOnly, liabilityGross, onToggle ?? (() => {}), onDelete);

    return (
        <StyledInvoicesTableWrapper>
            <Table<AppliedAdditionalCharge>
                columns={columns}
                dataSource={additionalCharges}
                pagination={false}
                bordered={false}
                size="small"
                rowKey="key"
                rowClassName={(record) => (!record.active ? 'row-disabled' : '')}
            />
        </StyledInvoicesTableWrapper>
    );
};

export default AppliedAdditionalChargesTable;
